'use client'

import { TabPanel, TabProvider } from '@ariakit/react'
import React, { Fragment } from 'react'
import styled from 'styled-components'

import { fromMd, untilMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'
import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import {
  GetPageTabSectionQuery,
  PageTabSectionFragmentFragment,
} from '../../../../../__generated__/graphql/catalog/graphql'
import { trackEvent } from '../../../../../lib/analytics/analytics'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import { NextLink } from '../../../navigation/NextLink'
import { TilesLayout } from '../../../tiles/Tiles.client'
import {
  PageSection,
  PageSectionContent,
  sectionSideGutterFromMdAndExpanded,
  sectionSideGutterUntilMd,
} from '../../PageSection.client'
import { getContentfulPageSectionAnalyticsName } from '../ContentfulPageSectionProvider.utils'

import {
  Badge,
  BadgeText,
  Body,
  LinkBody,
  PageTabTileImage,
  RawHtmlSection,
  StyledTab,
  StyledTextLink,
  StyledTile,
  TileBody,
  Title,
  TitleBar,
} from './ContentfulPageTabSection.styles'

const VISIBLE_TILES_DESKTOP = 7
const VISIBLE_TILES_GRID_TABLET = 3
const VISIBLE_TILES_MOBILE = 2

export const pageTabSectionFragment = gql(`
  fragment PageTabSectionFragment on PageTabSection {
    __typename
    id
    hideOnMobile
    hideOnDesktop
    name
    tabs {
      id
      tabSections {
        __typename
        ... on PageTabTileSection {
          __typename
          id
          layoutVariantMobile
          layoutVariantDesktop
          tiles {
            id
            ctaText
            href
            image
            badgeText
          }
          tileVariantMobile
          tileVariantDesktop
        }
        ... on RawHtmlSection {
          ...RawHtmlSectionFragment
        }
      }
      text
      textColor
    }
    title
  }
`)

export type ContentfulPageTabSectionAlignmentOverrides = {
  /**
   * Explicitly define whether or not the captions for individual tabs in this section should be center-aligned on mobile viewports.
   *
   * Defaults to true for non-square tiles, false for square tiles.
   */
  centerTextMobile?: boolean
  /**
   * Explicitly define whether or not the captions for individual tabs in this section should be center-aligned on desktop viewports.
   *
   * Defaults to true for non-square tiles, false for square tiles.
   */
  centerTextDesktop?: boolean
}

export type ContentfulPageTabSectionProps = {
  pageSectionIndex: number
  data: PageTabSectionFragmentFragment
} & ContentfulPageTabSectionAlignmentOverrides

export const ContentfulPageTabSection = ({
  data,
  pageSectionIndex,
  centerTextMobile,
  centerTextDesktop,
}: ContentfulPageTabSectionProps) => {
  const translations = useTranslation()

  const section = data
  const title = section.title
  const tabs = section.tabs

  const hideOnMobile = section.hideOnMobile
  const hideOnDesktop = section.hideOnDesktop

  const showTabs = tabs.length > 1

  const pageSectionDisplayName = title ?? ''
  const pageSectionAnalyticsName = getContentfulPageSectionAnalyticsName(section.__typename)

  return (
    <HideChildrenOn $mobile={hideOnMobile} $desktop={hideOnDesktop}>
      <PageSection hasVerticalMargin={true}>
        <PageSectionContent gutterSize={'none'}>
          <TabProvider defaultSelectedId={tabs[0]?.id}>
            <TitleBar>
              {title && (
                <Title asTag='h5' isUppercase={isUppercase(title)}>
                  {title}
                </Title>
              )}
              {showTabs && <TabButtons tabs={tabs} pageSectionIndex={pageSectionIndex} />}
            </TitleBar>
            <TabsContent
              centerTextMobile={centerTextMobile}
              centerTextDesktop={centerTextDesktop}
              tabs={tabs}
              scrollLabels={{
                forward: translations.magnolia.general.scrollForward,
                back: translations.magnolia.general.scrollBack,
              }}
              pageSectionAnalyticsName={pageSectionAnalyticsName}
              pageSectionDisplayName={pageSectionDisplayName}
              pageSectionIndex={pageSectionIndex}
            />
          </TabProvider>
        </PageSectionContent>
      </PageSection>
    </HideChildrenOn>
  )
}

const TabsContent: React.FC<
  {
    tabs: PageTabs
    scrollLabels: { forward: string; back: string }
    pageSectionIndex: number
    pageSectionDisplayName: string
    pageSectionAnalyticsName: string
  } & ContentfulPageTabSectionAlignmentOverrides
> = ({
  tabs,
  scrollLabels,
  pageSectionAnalyticsName,
  pageSectionDisplayName,
  pageSectionIndex,
  centerTextMobile,
  centerTextDesktop,
}) => {
  return tabs && tabs.length > 1
    ? tabs.map(tab => {
        return (
          <TabPanel tabId={tab.id} key={tab.id}>
            {tab.tabSections.map((tabSection, index) => {
              return (
                <Fragment key={tabSection.id}>
                  {tabSection.__typename === 'PageTabTileSection' ? (
                    <TabTileSection
                      centerTextDesktop={centerTextDesktop}
                      centerTextMobile={centerTextMobile}
                      pageSectionIndex={pageSectionIndex}
                      pageSectionDisplayName={pageSectionDisplayName}
                      pageSectionAnalyticsName={pageSectionAnalyticsName}
                      scrollLabels={scrollLabels}
                      key={`TabTileSection${index}`}
                      {...tabSection}
                    />
                  ) : (
                    //TODO.
                    <RawHtmlSection {...tabSection} />
                  )}
                </Fragment>
              )
            })}
          </TabPanel>
        )
      })
    : tabs[0]?.tabSections.map((tabSection, index) => {
        return (
          <Fragment key={tabSection.id}>
            {tabSection.__typename === 'PageTabTileSection' ? (
              <TabTileSection
                {...tabSection}
                centerTextDesktop={centerTextDesktop}
                centerTextMobile={centerTextMobile}
                scrollLabels={scrollLabels}
                key={`TabTileSection${index}`}
                pageSectionIndex={pageSectionIndex}
                pageSectionDisplayName={pageSectionDisplayName}
                pageSectionAnalyticsName={pageSectionAnalyticsName}
              />
            ) : (
              //TODO.
              <RawHtmlSection {...tabSection} />
            )}
          </Fragment>
        )
      })
}

const TabTileSection: React.FC<
  Extract<
    NonNullable<PageTabs[number]['tabSections'][number]>,
    { __typename: 'PageTabTileSection' }
  > & {
    scrollLabels: { forward: string; back: string }
    pageSectionIndex: number
    pageSectionDisplayName: string
    pageSectionAnalyticsName: string
  } & ContentfulPageTabSectionAlignmentOverrides
> = props => {
  const {
    tiles,
    layoutVariantDesktop: untypedLayoutVariantDesktop,
    layoutVariantMobile: untypedLayoutVariantMobile,
    tileVariantDesktop,
    tileVariantMobile,
    scrollLabels,
    pageSectionAnalyticsName,
    pageSectionDisplayName,
    pageSectionIndex,
    centerTextMobile,
    centerTextDesktop,
  } = props

  const layoutVariantMobile = untypedLayoutVariantMobile === 'grid' ? 'grid' : 'slider'
  const layoutVariantDesktop = untypedLayoutVariantDesktop === 'grid' ? 'grid' : 'slider'

  const visibleTiles = {
    sm: tiles.length < VISIBLE_TILES_MOBILE ? tiles.length : VISIBLE_TILES_MOBILE,
    md:
      tiles.length < VISIBLE_TILES_GRID_TABLET
        ? tiles.length
        : layoutVariantDesktop === 'grid'
        ? VISIBLE_TILES_GRID_TABLET
        : VISIBLE_TILES_DESKTOP,
    lg: tiles.length < VISIBLE_TILES_DESKTOP ? tiles.length : VISIBLE_TILES_DESKTOP,
  }
  return (
    <TilesLayout
      scrollBackLabel={scrollLabels.back}
      scrollForwardLabel={scrollLabels.forward}
      liftButtonsPixels={32}
      gutters={{
        sm: sectionSideGutterUntilMd,
        md: sectionSideGutterFromMdAndExpanded,
        lg: sectionSideGutterFromMdAndExpanded,
      }}
      layout={{
        sm: layoutVariantMobile,
        md: layoutVariantDesktop,
        lg: tiles.length >= VISIBLE_TILES_DESKTOP ? layoutVariantDesktop : 'grid',
      }}
      justifyContent={{
        sm: 'center',
        md: 'center',
        lg: 'center',
      }}
      visibleTiles={visibleTiles}
    >
      {tiles.map((tile, index) => {
        return (
          <PageTabSectionTile
            {...tile}
            centerTextMobile={centerTextMobile}
            centerTextDesktop={centerTextDesktop}
            visibleTiles={visibleTiles}
            tileVariantDesktop={tileVariantDesktop}
            tileVariantMobile={tileVariantMobile}
            layoutVariantDesktop={layoutVariantDesktop}
            layoutVariantMobile={layoutVariantMobile}
            key={`PageTabSectionTile${index}`}
            pageSectionDisplayName={pageSectionDisplayName}
            pageSectionAnalyticsName={pageSectionAnalyticsName}
            pageSectionIndex={pageSectionIndex}
          />
        )
      })}
    </TilesLayout>
  )
}

const PageTabSectionTile: React.FC<
  PageTabTileSection['tiles'][number] & {
    tileVariantDesktop: PageTabSectionTileVariant
    tileVariantMobile: PageTabSectionTileVariant
    layoutVariantDesktop: PageTabSectionLayoutVariant
    layoutVariantMobile: PageTabSectionLayoutVariant
    pageSectionIndex: number
    pageSectionDisplayName: string
    pageSectionAnalyticsName: string
    centerText?: ''
    visibleTiles: { sm: number; md: number; lg?: number }
  } & ContentfulPageTabSectionAlignmentOverrides
> = ({
  ctaText,
  href,
  image,
  tileVariantDesktop,
  tileVariantMobile,
  layoutVariantDesktop,
  layoutVariantMobile,
  pageSectionAnalyticsName,
  pageSectionDisplayName,
  pageSectionIndex,
  centerTextMobile = tileVariantMobile !== 'square',
  centerTextDesktop = tileVariantDesktop !== 'square',
  badgeText,
  visibleTiles,
}) => {
  const constrainedTileWidths = {
    sm: tileVariantMobile === 'square' && layoutVariantMobile !== 'grid' ? 140 : undefined,
    md: tileVariantDesktop === 'square' && layoutVariantDesktop !== 'grid' ? 140 : undefined,
  }
  return (
    <StyledTile visibleTiles={visibleTiles} $constrainedTileWidths={constrainedTileWidths}>
      <TileBody $tileVariantDesktop={tileVariantDesktop} $tileVariantMobile={tileVariantMobile}>
        <NextLink
          href={href}
          {...trackEvent({
            action: 'click tile',
            category: pageSectionAnalyticsName,
            contentfulName: pageSectionDisplayName,
            label: ctaText,
            value: `badge: ${badgeText}`,
            correspondingAsset: href,
            pageSectionIndex,
          })}
        >
          {badgeText && (
            <Badge>
              <BadgeText isUppercase={isUppercase(badgeText)}>{badgeText}</BadgeText>
            </Badge>
          )}
          <PageTabTileImage
            $tileVariantDesktop={tileVariantDesktop}
            $tileVariantMobile={tileVariantMobile}
            alt={ctaText}
            aspectRatios={{
              sm: tileVariantMobile === 'rectangle' ? null : 1,
              md: tileVariantDesktop === 'rectangle' ? null : 1,
            }}
            src={image}
            loading='lazy'
            widths={{
              unit: 'vw',
              sm: 100 / 2,
              md: 100 / 8,
            }}
          />
        </NextLink>
        <LinkBody
          $isUppercase={isUppercase(ctaText)}
          $centerTextMobile={centerTextMobile}
          $centerTextDesktop={centerTextDesktop}
          $gridMobile={layoutVariantMobile === 'grid'}
          $gridDesktop={layoutVariantDesktop === 'grid'}
        >
          <StyledTextLink
            {...trackEvent({
              action: 'click tile',
              category: pageSectionAnalyticsName,
              contentfulName: pageSectionDisplayName,
              label: ctaText,
              correspondingAsset: href,
              pageSectionIndex,
            })}
            title={ctaText}
            variant='underline-on-hover'
            inheritTypography
            href={href}
          >
            {ctaText}
          </StyledTextLink>
        </LinkBody>
      </TileBody>
    </StyledTile>
  )
}

const TabButtons: React.FC<{ tabs: PageTabs; pageSectionIndex: number }> = ({
  tabs,
  pageSectionIndex,
}) => {
  return (
    <Body>
      {tabs.map(tab => {
        return (
          <StyledTab
            key={tab.id}
            id={tab.id}
            $textColor={tab.textColor}
            {...trackEvent({
              action: 'click tab',
              category: 'page-tab-section',
              label: tab.text,
              pageSectionIndex,
            })}
            $isUppercase={isUppercase(tab.text)}
          >
            {tab.text}
          </StyledTab>
        )
      })}
    </Body>
  )
}

export type PageTabSection = Extract<
  NonNullable<GetPageTabSectionQuery['pageViewSection']>,
  { __typename: 'PageTabSection' }
>

export type PageTabs = NonNullable<PageTabSection['tabs']>

export type PageTabTileSection = Extract<
  NonNullable<PageTabs[number]['tabSections'][number]>,
  { __typename: 'PageTabTileSection' }
>

export type PageTabSectionTileVariant =
  | PageTabTileSection['tileVariantMobile']
  | PageTabTileSection['layoutVariantDesktop']
export type PageTabSectionLayoutVariant =
  | PageTabTileSection['layoutVariantMobile']
  | PageTabTileSection['layoutVariantDesktop']

const HideChildrenOn = styled.div<{
  $mobile?: boolean
  $desktop?: boolean
}>`
  ${untilMd} {
    display: ${o => o.$mobile && 'none'};
  }

  ${fromMd} {
    display: ${o => o.$desktop && 'none'};
  }
`
